import DayPlanTrackingActionButton from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/ActionButton";
import { getStatus } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/utils";
import {
  DayPlanTrackingType,
  TableColumns,
} from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/types";
import { getPhoneNumberWithoutNOPrefix } from "features/Header/PuzzelTelephone/utils";
import { FC } from "react";
import { CallButton } from "shared/components/buttons/CallButton";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { TableCell, TableRow } from "shared/components/Spor";
import { formatDayDateString } from "shared/utils/dateTimeUtils";
import styled, { DefaultTheme, useTheme } from "styled-components";

const getTextColor = (theme: DefaultTheme, severityStatus: string) => {
  switch (severityStatus) {
    case "WARN":
      return theme.colorWarningTextMain;
    case "ERROR":
      return theme.colorAlarmTextMain;
    case "OK":
      return theme.colorTextMain;
    default:
      return "inherit";
  }
};

const getBackgroundColor = (theme: DefaultTheme, severityStatus: string) => {
  switch (severityStatus) {
    case "WARN":
      return theme.colorWarningFillPulse;
    case "ERROR":
      return theme.colorAlarmFillPulse;
    case "OK":
      return theme.colorSuccessFillPulse;
    default:
      return "inherit";
  }
};

const getBorderColor = (theme: DefaultTheme, severityStatus: string) => {
  switch (severityStatus) {
    case "WARN":
      return theme.colorWarningOutline;
    case "ERROR":
      return theme.colorAlarmOutline;
    case "OK":
      return theme.colorSuccessOutline;
    default:
      return theme.colorOutlineSecondary;
  }
};

/**
 * This fades the component when a children gets the data-fade attribute
 */
const StyledTr = styled(TableRow)`
  transition: opacity 800ms ease-in-out;
  &:has(button[data-success="true"]) {
    background-color: ${({ theme }) =>
      theme.colorSuccessFill} !important; // !important is needed to override the hover color
    outline: ${({ theme }) => theme.colorSuccessOutline};
    color: ${({ theme }) => theme.colorTextMain};
  }
  &:has(button[data-fade="true"]) {
    transition: none;
    opacity: 0;
    &&& * {
      transition: all 800ms ease-in-out;
      font-size: 0px;
      max-height: 0px;
      border: none;
      padding: 0;
      margin: 0;
    }
  }
`;

const ForceOverrideButtonStyles = styled.div<{ overrideColor: string }>`
  &&& button {
    outline-color: ${(props) => props.overrideColor} !important;
    border-color: ${(props) => props.overrideColor} !important;
    color: ${(props) => props.overrideColor} !important;
  }
`;

type TableProps = {
  filteredDayPlanTrackings: DayPlanTrackingType[];
  onRowClick: (dayPlanTrackingType: DayPlanTrackingType) => void;
};

export const TableContent: FC<TableProps> = ({
  filteredDayPlanTrackings,
  onRowClick,
}) => {
  const theme = useTheme();

  return filteredDayPlanTrackings.length > 0 ? (
    <>
      {filteredDayPlanTrackings.map((it) => {
        const status = getStatus(it.status);
        return (
          <StyledTr
            cursor="pointer"
            key={`${it.employeeId}-${it.dayPlanDate}`}
            color={getTextColor(theme, status.severity)}
            backgroundColor={getBackgroundColor(theme, status.severity)}
            css={{
              "& td": {
                padding: "6px 12px",
                border: "none",
                borderBottom: `1px solid ${getBorderColor(theme, status.severity)}`,
              },
            }}
            _hover={{ filter: theme.filterHover }}
            onClick={() => onRowClick(it)}
          >
            <TableCell>{status.text}</TableCell>
            <TableCell>{it.companyCode}</TableCell>
            <TableCell>{formatDayDateString(it.lastUpdatedAt)}</TableCell>
            <TableCell>{formatDayDateString(it.dutyStart)}</TableCell>
            <TableCell>
              {it.fullName
                ? `${it.fullName} (${it.employeeId})`
                : it.employeeId}
            </TableCell>
            <TableCell>{`${it.isConductor ? "K" : ""} ${it.isDriver ? "L" : ""}`}</TableCell>
            <TableCell>{`${it.dutyId} ${it.deletion ? "(slettet)" : ""}`}</TableCell>
            <TableCell>
              {it.phoneNumber && (
                <ForceOverrideButtonStyles
                  overrideColor={getTextColor(theme, status.severity)}
                >
                  <CallButton
                    phoneNumber={getPhoneNumberWithoutNOPrefix(it.phoneNumber)}
                    size="xs"
                  />
                </ForceOverrideButtonStyles>
              )}
            </TableCell>
            <TableCell>
              <ForceOverrideButtonStyles
                overrideColor={getTextColor(theme, status.severity)}
              >
                <DayPlanTrackingActionButton
                  status={it.status}
                  employeeId={it.employeeId}
                />
              </ForceOverrideButtonStyles>
            </TableCell>
          </StyledTr>
        );
      })}
    </>
  ) : (
    <TableRow>
      <TableCell colSpan={TableColumns.length} borderBottom="none">
        <NoDataMessage reason="Fant ingen tjenestevarslinger som matcher aktivt filter" />
      </TableCell>
    </TableRow>
  );
};
